import React from "react";
import { Paper, Box, alpha, useTheme, Typography } from "@mui/material";
import { ShoppingCart, AddShoppingCart } from "@mui/icons-material";
import CartHeader from "./CartHeader";
import CartFooter from "./CartFooter";
import CartItemCard from "./CartItemCard";

/** Width of the order summary panel — keep in sync with POS layout overlays */
export const CART_SUMMARY_WIDTH = 420;

/**
 * CartSummary Component
 * Main container for order summary with header, items list, and footer
 */
const CartSummary = ({
  cart,
  onAddToCart,
  onRemoveFromCart,
  onDeleteFromCart,
  calculateTotal,
  onPlaceOrder,
  buttonLabel = "Place Order",
  addingModeLabel = null,
}) => {
  const theme = useTheme();
  const itemCount = cart.length;
  const total = calculateTotal();

  return (
    <Paper
      elevation={0}
      sx={{
        width: CART_SUMMARY_WIDTH,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderLeft: `2px solid ${alpha(theme.palette.divider, 0.15)}`,
        bgcolor: "#fafafa",
        minHeight: 0, // Allows proper flex shrinking
        flexShrink: 0, // Prevent cart from shrinking
      }}
    >
      {/* Header */}
      <CartHeader itemCount={itemCount} />

      {/* Adding-to-order mode banner */}
      {addingModeLabel && (
        <Box
          sx={{
            px: 2,
            py: 1,
            bgcolor: alpha(theme.palette.warning.main, 0.1),
            borderBottom: `2px solid ${alpha(theme.palette.warning.main, 0.3)}`,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <AddShoppingCart
            sx={{ fontSize: 18, color: theme.palette.warning.dark }}
          />
          <Typography
            variant="caption"
            sx={{ fontWeight: 700, color: theme.palette.warning.dark }}
          >
            {addingModeLabel}
          </Typography>
        </Box>
      )}

      {/* Cart Items */}
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          px: 1.25,
          py: 1,
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-track": {
            bgcolor: alpha(theme.palette.grey[300], 0.3),
            borderRadius: "10px",
          },
          "&::-webkit-scrollbar-thumb": {
            bgcolor: theme.palette.primary.main,
            borderRadius: "10px",
            "&:hover": {
              bgcolor: theme.palette.primary.dark,
            },
          },
        }}
      >
        {cart.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              py: 8,
            }}
          >
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                bgcolor: alpha(theme.palette.grey[400], 0.1),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 2,
              }}
            >
              <ShoppingCart
                sx={{
                  fontSize: 40,
                  color: alpha(theme.palette.text.secondary, 0.4),
                }}
              />
            </Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: theme.palette.text.secondary,
                mb: 0.5,
              }}
            >
              Cart is empty
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontWeight: 500 }}
            >
              Add items to get started
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
            {cart.map((item, index) => (
              <CartItemCard
                key={
                  item.isOffer
                    ? item.offerId
                    : item.cartItemId || item.id || index
                }
                item={item}
                onAdd={onAddToCart}
                onRemove={onRemoveFromCart}
                onDelete={onDeleteFromCart}
              />
            ))}
          </Box>
        )}
      </Box>

      {/* Footer */}
      <Box sx={{ flexShrink: 0 }}>
        <CartFooter
          total={total}
          itemCount={itemCount}
          onPlaceOrder={onPlaceOrder}
          disabled={cart.length === 0}
          buttonLabel={buttonLabel}
        />
      </Box>
    </Paper>
  );
};

export default CartSummary;
